/**
 * The dock's "Copy table": the per-bin clearance table as TSV, for a paste
 * into a spreadsheet.
 *
 * The FILE export stays the worker's own CSV (ruling: re-deriving a download
 * in the browser would be a second implementation free to disagree with the
 * file in the batch panel). A clipboard copy is a different job — it is
 * selection-aware, so it cannot be that file — and it is held to the file's
 * shape instead: the same columns, in the same order, with the same decimals
 * and the same empty cell for a figure that does not exist.
 *
 * TWIN: `_CSV_COLUMNS` and the row writer in `clearance_csv`
 * (`scripts/clearance.py`). Change a column there and change it here.
 */
import type { ClearanceBin } from './types';

export const CLEARANCE_TSV_COLUMNS = [
  'sta_start_display',
  'sta_end_display',
  'sta_start_internal',
  'sta_end_internal',
  'length_m',
  'point_count',
  'violation_count',
  'max_intrusion_m',
  'worst_offset_m',
  'worst_height_m',
  'near_miss_0_1m',
  'near_miss_0_2m',
  'near_miss_0_35m',
] as const;

/** The worker's `n(v, dec)`: fixed decimals, `-0` folded to `0`, empty for null. */
const n = (v: number | null, dec: number): string => {
  if (v === null || !Number.isFinite(v)) return '';
  const r = Number(v.toFixed(dec));
  return (r === 0 ? 0 : r).toFixed(dec);
};

export function clearanceBinsToTsv(bins: readonly ClearanceBin[]): string {
  const lines = [CLEARANCE_TSV_COLUMNS.join('\t')];
  for (const b of bins) {
    lines.push(
      [
        n(b.staStartDisplayM, 2),
        n(b.staEndDisplayM, 2),
        n(b.staStartInternalM, 2),
        n(b.staEndInternalM, 2),
        n(b.staEndInternalM - b.staStartInternalM, 2),
        String(b.pointCount),
        String(b.violationCount),
        n(b.maxIntrusionM, 4),
        n(b.worstOffsetM, 3),
        n(b.worstHeightM, 3),
        String(b.nearMiss.within0_1M),
        String(b.nearMiss.within0_2M),
        String(b.nearMiss.within0_35M),
      ].join('\t'),
    );
  }
  return lines.join('\n');
}
